import {
  Button,
  Chip,
  Divider,
  EButtonColorVariant,
  EChipSizeVariant,
  LinkButton,
} from '@outdoorsyco/bonfire';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import DatePicker from '@/components/common/DatePicker/DatePicker';
import { VirtualizedDatePicker } from '@/components/common/VirtualizedDatePicker/VirtualizedDatePicker';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useInitialDates } from '@/hooks/useInitialInputs';
import { IHeaderSearchFilterDates, setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { getHeaderSearchDatesFilter } from '@/redux/selectors/globalHeader';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';
import { trackEvent } from '@/services/track-event';

import { FilterContent, TFilterContentSharedProps } from './FilterContent';

type TDateFilterContentProps = TFilterContentSharedProps & {
  onDataSelection?: () => void;
  onTrackFilterToggled?: (
    oldSelection: Record<string, unknown>,
    newSelection: Record<string, unknown>,
    modifiedWidgetFilter: ESearchWidgetFilter,
  ) => void;
  allowSameDay?: boolean;
};

export const DateFilterContent = ({
  onDataSelection,
  onTrackFilterToggled,
  allowSameDay,
  onBack,
  ...props
}: TDateFilterContentProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { isAboveDesktop } = useBreakpoint();

  const dates = useSelector(getHeaderSearchDatesFilter);

  const { hasChanges, isInResetState, resetToInitial } = useInitialDates(
    {
      from: dates?.from,
      to: dates?.to,
      flexible_days: dates?.flexible_days,
    },
    props.isOpened,
  );

  const changeDatesSelection = (selection: IHeaderSearchFilterDates) => {
    dispatch(
      setHeaderSearchFilter({
        dates: selection,
      }),
    );

    onTrackFilterToggled?.({ ...dates }, { ...selection }, ESearchWidgetFilter.Date);

    trackEvent({
      event: 'search/filter/dates',
      action: 'update',
      before: {
        'date[from]': dates?.from ? dayjs(dates.from).format('YYYY-MM-DD') : undefined,
        'date[to]': dates?.to ? dayjs(dates.to).format('YYYY-MM-DD') : undefined,
      },
      after: {
        'date[from]': selection?.from ? dayjs(selection.from).format('YYYY-MM-DD') : undefined,
        'date[to]': selection?.to ? dayjs(selection.to).format('YYYY-MM-DD') : undefined,
      },
    });
  };

  return (
    <FilterContent
      onBack={() => {
        const initial = resetToInitial();
        changeDatesSelection({
          from: initial.from,
          to: initial.to,
          flexible_days: initial.flexible_days,
        });
        onDataSelection?.();
        onBack?.();
      }}
      header={intl.formatMessage({
        defaultMessage: 'Select your dates',
        id: 'V1vyH+',
        description: 'Home Search Widget > Dates Modal Title',
      })}
      content={
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-grow overflow-auto">
            <FilterDatePicker
              desktop={isAboveDesktop}
              from={dates?.from}
              to={dates?.to}
              onChange={(from, to) => {
                if (to && from) {
                  changeDatesSelection({
                    ...dates,
                    from,
                    to,
                  });

                  onDataSelection?.();
                }
              }}
              allowSameDay={allowSameDay}
            />
          </div>
        </div>
      }
      footerContent={
        <FilterDatePickerFooter
          onFlexibleDaysChange={flexibleDays => {
            changeDatesSelection({
              ...dates,
              flexible_days: flexibleDays,
            });
          }}
          onClearDates={() => {
            changeDatesSelection({});
          }}
          onDone={() => {
            props.onDone?.();
          }}
          hasChanges={hasChanges}
          isInResetState={isInResetState}
        />
      }
      {...props}
    />
  );
};

type TFilterDatePickerProps = {
  desktop?: boolean;
  from?: Date;
  to?: Date;
  onChange?: (from?: Date, to?: Date) => void;
  allowSameDay?: boolean;
};

const FilterDatePicker = ({
  desktop,
  from: initialFrom,
  to: initialTo,
  onChange,
  allowSameDay,
}: TFilterDatePickerProps) => {
  const [selectedDates, setSelectedDates] = useState({
    from: initialFrom,
    to: initialTo,
  });

  // Keep in sync if we update from outside
  useEffect(() => {
    setSelectedDates({
      from: initialFrom,
      to: initialTo,
    });
  }, [initialFrom, initialTo]);

  const [initialMonth, setInitialMonth] = useState(initialFrom);

  const toBeDisabled = useMemo(
    () => new Date(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()),
    [],
  );

  return (
    <div>
      {desktop ? (
        <DatePicker
          range
          numberOfMonths={2}
          onChange={selection => {
            setSelectedDates(selection);
            onChange?.(selection?.from, selection?.to);
          }}
          disableBefore={toBeDisabled}
          dateTo={selectedDates.to}
          dateFrom={selectedDates.from}
          fromMonth={dayjs().toDate()}
          toMonth={dayjs().add(2, 'year').toDate()}
          month={initialMonth}
          onMonthChange={setInitialMonth}
          allowSameDay={allowSameDay}
        />
      ) : (
        <VirtualizedDatePicker
          dateFrom={selectedDates.from}
          dateTo={selectedDates.to}
          onChange={selection => {
            setSelectedDates(selection);
            onChange?.(selection?.from, selection?.to);
          }}
          disableBefore={toBeDisabled}
          fromMonth={dayjs().toDate()}
          allowSameDay={allowSameDay}
          totalMonths={24}
          activeMonth={initialMonth}
          onMonthChange={setInitialMonth}
        />
      )}
    </div>
  );
};

type TFilterDatePickerFooterProps = {
  onFlexibleDaysChange: (flexibleDays?: number) => void;
  onClearDates: () => void;
  onDone: () => void;
  hasChanges: boolean;
  isInResetState: boolean;
};

const FilterDatePickerFooter = ({
  onFlexibleDaysChange,
  onClearDates,
  onDone,
  hasChanges,
  isInResetState,
}: TFilterDatePickerFooterProps) => {
  const intl = useIntl();
  const hasSingleAction = [onClearDates, onDone].filter(x => x).length === 1;

  const dates = useSelector(getHeaderSearchDatesFilter);

  return (
    <div className="w-full">
      <>
        <Divider />
        <div className="flex items-center justify-between p-4 gap-10 lg:pb-0">
          <div className="hidden lg:block">
            <LinkButton
              label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
              onClick={onClearDates}
            />
          </div>
          <div className="flex p-1 overflow-x-auto scrollbar-none gap-4">
            <Chip
              content={intl.formatMessage({ defaultMessage: 'Match exact dates', id: 'uhcpp1' })}
              sizeVariant={EChipSizeVariant.Small}
              onClick={() => onFlexibleDaysChange(undefined)}
              active={!dates?.flexible_days}
              withBoxShadow={false}
              contentClassName="whitespace-nowrap [&>p]:font-medium"
            />
            <Chip
              content={intl.formatMessage({ defaultMessage: '± 1 day', id: '9IdT1V' })}
              sizeVariant={EChipSizeVariant.Small}
              onClick={() => onFlexibleDaysChange(1)}
              active={dates?.flexible_days === 1}
              withBoxShadow={false}
              contentClassName="whitespace-nowrap [&>p]:font-medium"
            />
            <Chip
              content={intl.formatMessage({ defaultMessage: '± 3 days', id: 'r4uykC' })}
              sizeVariant={EChipSizeVariant.Small}
              onClick={() => onFlexibleDaysChange(3)}
              active={dates?.flexible_days === 3}
              withBoxShadow={false}
              contentClassName="whitespace-nowrap [&>p]:font-medium"
            />
          </div>
        </div>
        <Divider className="lg:hidden" />
      </>
      <div
        className={`flex items-center ${hasSingleAction ? 'justify-end' : 'justify-between'} p-4 lg:hidden`}>
        {onClearDates && (
          <LinkButton
            label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
            onClick={onClearDates}
            disabled={isInResetState}
          />
        )}
        {onDone && (
          <Button
            variant={EButtonColorVariant.Primary}
            label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
            onClick={onDone}
            disabled={!hasChanges}
          />
        )}
      </div>
    </div>
  );
};
